// src/lib/map-overlay/screenLabels.js
//
// Text on a PadPlanner map is DOM, not symbol layers: the PadPlanner style
// has no glyphs. These are the overlay's two halves, out of the
// FootprintEditor (S4b) so the dashboard's plan pins (S6a2) use the same
// code rather than a copy:
//
//   cullLabels  project each label's point, skip it off screen (with a
//               margin), keep one per collision cell, stop at the cap
//   LabelPool   DOM elements reused across renders: a pan re-projects and
//               repaints them, it never builds or removes one
//
// The caller caches each label's point when its data changes (not on every
// pan) and calls cullLabels + pool.render on each map move.
//
// COLLISION. By default a label takes the one cell its point falls in, as
// the editor has always done: one label per cell, so two can still touch
// across a cell edge. With `box` (the label's rectangle relative to its
// point) a label takes every cell its rectangle covers and is kept only if
// all are free. Two rectangles that overlap share a cell, so boxed labels
// never overlap.

/**
 * @param {object[]} items                 each with a point (`at(item)`, default item.lngLat)
 * @param {object} o
 * @param {(lngLat: number[]) => {x: number, y: number}} o.project
 * @param {number} o.width                 the viewport, px
 * @param {number} o.height
 * @param {{w: number, h: number}} o.cell  collision cell, px
 * @param {number} o.max                   at most this many kept
 * @param {number} [o.margin]              px beyond the viewport still drawn
 * @param {{dx: number, dy: number, w: number, h: number}|null} [o.box]
 * @param {(item: object) => number[]} [o.at]
 * @returns {{item: object, x: number, y: number}[]}  in the items' order
 */
export function cullLabels(items, { project, width, height, cell, max, margin = 0, box = null, at = (i) => i.lngLat }) {
  const taken = new Set();
  const out = [];
  for (const item of items) {
    if (out.length >= max) break;
    const p = project(at(item));
    if (p.x < -margin || p.y < -margin || p.x > width + margin || p.y > height + margin) continue;
    const cells = box ? cellsUnder(p.x + box.dx, p.y + box.dy, box.w, box.h, cell) : [`${Math.floor(p.x / cell.w)}:${Math.floor(p.y / cell.h)}`];
    if (cells.some((c) => taken.has(c))) continue;
    for (const c of cells) taken.add(c);
    out.push({ item, x: p.x, y: p.y });
  }
  return out;
}

// The cells a w×h rectangle at (x, y) covers; an edge that only touches the
// next cell does not take it.
function cellsUnder(x, y, w, h, cell) {
  const out = [];
  const c0 = Math.floor(x / cell.w), c1 = Math.floor((x + w - 1e-6) / cell.w);
  const r0 = Math.floor(y / cell.h), r1 = Math.floor((y + h - 1e-6) / cell.h);
  for (let c = c0; c <= c1; c++) for (let r = r0; r <= r1; r++) out.push(`${c}:${r}`);
  return out;
}

/**
 * Pooled elements in `parent`. `make()` builds one; `render(shown, paint)`
 * shows one per entry (painting it with `paint(el, entry, i)`) and hides the
 * rest. `size` builds that many up front, so a render up to it creates none.
 */
export class LabelPool {
  constructor(parent, { make, size = 0 }) {
    this.parent = parent;
    this.make = make;
    this.elements = [];
    this._grow(size);
  }

  _grow(n) {
    while (this.elements.length < n) {
      const el = this.make();
      el.style.display = "none";
      this.parent.appendChild(el);
      this.elements.push(el);
    }
  }

  render(shown, paint) {
    this._grow(shown.length);
    this.elements.forEach((el, i) => {
      const s = shown[i];
      if (!s) { if (el.style.display !== "none") el.style.display = "none"; return; }
      paint(el, s, i);
      if (el.style.display === "none") el.style.display = "";
    });
  }
}
